import { AboutData } from '../types';

const aboutData: AboutData = {
  title: "Ich erzähle Dir was über mich.",
  intro: [
    "Ich bin Robin Wichmann, Software-Entwickler aus Leidenschaft, seit über {yearsExp} Jahren. Gelernt in Hamburger Agenturen, mit Projekten für Marken und Konzerne von Volkswagen über IKEA bis Netflix. Heute baue ich produktive KI-Features und eigene Produkte, darunter mehrere Apps im Apple App Store.",
    "Still stehe ich dabei nie: Mich treibt der Reiz, Neues zu lernen, indem ich es baue. Im Kern bin ich Product Builder, vom Konzept bis in den Store. Und ich arbeite KI-nativ: geplant und umgesetzt mit KI-Agenten, abgesichert durch Tests, Mutationstests und automatisierte Pipelines.",
    "Ein paar Prinzipien ziehen sich durch alles, was ich baue: Datenschutz statt Datenhunger, Open Source statt Lock-in, KI so automatisiert wie möglich, mit dem Menschen in der Schleife, wo seine Entscheidung zählt. Dazu eine Vorliebe für visuelle Effekte und perfekte Ästhetik, nie auf Kosten von Performance und Barrierefreiheit."
  ],
  buttons: {
    work: "Meine Arbeiten",
    contact: "Kontakt"
  },
  skillsTitle: "Meine Skills & Expertise",
  skillCategories: [
    {
      name: "KI & LLM-Engineering",
      skills: [
        "LLM-Integration & Prompt Engineering",
        "Lokale LLMs (MLX, Ollama, vLLM)",
        "Agentische Workflows & Pipelines",
        "Retrieval-Augmented Generation (RAG)",
        "Embeddings & Semantische Suche",
        "Streaming / SSE & Structured Output",
        "Token- & Kostenoptimierung"
      ]
    },
    {
      name: "Frontend & Web",
      skills: [
        "TypeScript",
        "React",
        "Next.js",
        "Tailwind CSS",
        "HTML & CSS",
        "Sass/SCSS",
        "Responsive Design",
        "SEO & Pagespeed"
      ]
    },
    {
      name: "Backend & Daten",
      skills: [
        "Node.js",
        "PostgreSQL",
        "Prisma",
        "REST-APIs",
        "Echtzeit (Socket.io)",
        "Python"
      ]
    },
    {
      name: "Mobile & native Apps",
      skills: [
        "Swift & SwiftUI",
        "Kotlin & Jetpack Compose",
        "Flutter",
        "App-Store-Releases (Fastlane, TestFlight)",
        "Push-Infrastruktur (APNs, FCM)",
        "Ende-zu-Ende-Verschlüsselung"
      ]
    },
    {
      name: "DevOps & Infrastruktur",
      skills: [
        "Docker",
        "Coolify & Self-Hosting",
        "Git & CI",
        "S3 Object Storage",
        "Testing (Vitest, Cypress)"
      ]
    },
    {
      name: "Methoden & Leadership",
      skills: [
        "Teamführung & Tech Lead",
        "Agile Methoden",
        "UX/UI Design",
        "E-Commerce",
        "Mentoring & Recruiting"
      ]
    }
  ],
  activityTitle: "Code-Aktivität",
  activityNote: "Stand: Juli 2026",
  activityItems: [
    {
      name: "GitHub",
      description: "Eigene Projekte & Open Source: 1.800+ Contributions im letzten Jahr",
      image: "/assets/img/about/github_activity.webp",
      imageWidth: 1500,
      imageHeight: 345,
      accent: "green",
      link: "https://github.com/Marvbuster"
    },
    {
      name: "GitLab",
      description: "Berufliche Entwicklung: Issues, Merge Requests, Pushes und Reviews",
      image: "/assets/img/about/gitlab_activity.webp",
      imageWidth: 1790,
      imageHeight: 390,
      accent: "blue"
    }
  ],
  careerTitle: "Mein Werdegang",
  careerTimeline: [
    {
      year: "Oktober 2025 - Heute",
      title: "AI Developer",
      company: "apo.com Group GmbH",
      description: "Entwicklung produktiver KI-Features end-to-end, von lokaler Inferenz auf Apple Silicon über deterministische LLM-Integration bis zum Frontend. Fokus auf Zuverlässigkeit, Kostenkontrolle und Multi-Service-Architekturen.",
      location: "Markkleeberg",
      tasks: [
        "Fullstack-Produktentwicklung: Next.js-Frontend, mehrere Backend-Services, lokale Inferenz und Deployment in einer zusammenhängenden Architektur",
        "Deterministische LLM-Programmierung mit JSON-Schema-Validierung, Structured Output via Streaming und anti-halluzinationsbasiertem Prompt-Design",
        "Lokale LLMs auf Apple Silicon via MLX (mlx_lm.server, mlx_vlm.server), inkl. Prompt-Cache-Tuning und Modell-Evaluation",
        "Multi-Service-Architektur mit Bearer-/JWT-/X-API-KEY-Auth-Chain und OpenAI-kompatibler Proxy-Schicht über 50+ REST-Endpoints",
        "Streaming/SSE-basierte Echtzeit-Workflows: pg_notify-getriggerte Events, ReadableStream-Tee für Auto-Persist, Token-Streaming zum Browser",
        "Agentische Workflows mit priorisierten Background-Workern, getriggert über Server-Sent Events",
        "Token-Usage-Tracking und Kostenoptimierung mit tok/s- und tok/h-Dashboards, Request-Level-Metriken und DB-weiter Aggregation",
        "Shopspezifisches Prompt-Engineering inkl. Knowledge-Injection, Kundenkontext-Handling und verstärkter Sprachsteuerung"
      ],
      companyType: "Arzneimittel-Versandhändler"
    },
    {
      year: "Mai 2024 - September 2025",
      title: "Teamleiter / Tech Lead",
      company: "apo.com Group GmbH",
      description: "Leitung des Frontend-Teams, Verantwortung für Webtechnologien und E-Commerce-Lösungen. Strategische Planung und Koordination von Entwicklungsprojekten.",
      location: "Markkleeberg",
      tasks: [
        "Teamführung und Personalverantwortung inkl. Recruiting und Hiring-Prozesse",
        "Mentoring und Coaching von Teammitgliedern",
        "Technische Bewertung von Machbarkeiten und Ressourcenplanung",
        "Consulting für andere Fachbereiche und Gewerke",
        "Strategisches Prototyping für neue Technologien und Lösungsansätze",
        "Organisation von Fortbildungsmaßnahmen und Wissenstransfer",
        "Technische Entscheidungsfindung und Architektur-Design",
        "Koordination zwischen Produktmanagement und Entwicklungsteam"
      ],
      companyType: "Arzneimittel-Versandhändler"
    },
    {
      year: "März 2023 - Heute",
      title: "Frontend Developer",
      company: "apo.com Group GmbH",
      description: "Ich bin verantwortlich für die Frontend-Entwicklung und Optimierung diverser Online-Shops und Middleware-Implementierungen, mit Fokus auf React, Mobile App Development und SEO.",
      location: "Markkleeberg",
      tasks: [
        "Konzeption, Entwicklung und Betreuung diverser Online-Shops und Middleware Implementierungen",
        "Dynamische React Komponenten",
        "SEO und Pagespeed Optimierungen",
        "Definition von Code Conventions für das Team bzgl. Typescript und OOP",
        "Mobile und Desktop App Development",
        "Engineering (Webpack, Vite, Parcel…)",
        "Diverse Prototypen (u.A. Canvas, React, Vue, Storybook, StoryToDesign, Firebase Auth & RT Database)",
        "Flutter, Titanium",
        "Anlegen und Analyse von Tags und Events für GTM und GA",
        "Marketingtools und Kampagnen"
      ],
      companyType: "Arzneimittel-Versandhändler"
    },
    {
      year: "Februar 2017 - März 2023",
      title: "Senior Frontend Developer",
      company: "Plan A Internet",
      description: "Als Senior Frontend Developer bin ich zuständig für die Konzeption und Entwicklung von modernen Webseiten, Single Page Applications und Server-First Components mit Schwerpunkt auf Performance und Nutzererfahrung.",
      location: "Hamburg",
      tasks: [
        "Konzeption, Entwicklung und Betreuung klassischer Webseiten, SPA, SFC",
        "Implementierung dynamischer Trackinglösungen",
        "SEO"
      ],
      clients: ["TV-Spielfilm", "Fit for Fun", "FRoSTA", "tesa", "Hapag Lloyd", "Gruner + Jahr"],
      companyType: "Werbeagentur"
    },
    {
      year: "Oktober 2015 - Januar 2017",
      title: "Freelance Web Developer",
      company: "Freiberuflich",
      description: "Als freiberuflicher Webentwickler habe ich für renommierte Kunden moderne Webseiten und Anwendungen entwickelt, von der Konzeption bis zur finalen Umsetzung.",
      location: "Hamburg",
      tasks: [
        "Konzeption und Entwicklung klassischer Webseiten und Single Page Applications",
        "Implementierung dynamischer Trackinglösungen",
        "Werbemittel-Entwicklung"
      ],
      clients: ["Volkswagen", "Netflix", "Mercedes", "tesa", "Yogi Tea"]
    },
    {
      year: "Januar 2012 - Oktober 2015",
      title: "Flash Developer / Web Developer",
      company: "Grabarz & Partner Werbeagentur",
      description: "Bei Grabarz & Partner war ich verantwortlich für die Entwicklung innovativer Web-Specials und Landingpages für den internationalen Markt, sowie für die Betreuung der Auszubildenden und Organisation von Entwickler-Events.",
      location: "Hamburg",
      tasks: [
        "Konzeption und Entwicklung dynamischer Web-Specials und Landingpages; mehrsprachig für den internationalen Markt",
        "Werbemittel-Entwicklung",
        "Betreuung der Auszubildenden",
        "Organisation öffentlicher Events als Möglichkeit des Recruitungs (\"Code in the Dark\")"
      ],
      clients: ["Volkswagen", "Volkswagen Nutzfahrzeuge", "IKEA", "Wieners & Wieners"],
      companyType: "Werbeagentur"
    },
    {
      year: "September 2009 - November 2011",
      title: "Flash Developer",
      company: "deepblue networks",
      description: "Bei deepblue networks lag mein Fokus auf der Entwicklung dynamischer Webanwendungen (PWA) und mobiler Anwendungen mit Adobe AIR (3D), sowie der Konzeption und Umsetzung von Web-Anwendungen für bekannte Marken. Spieleentwicklung für Red Bull und Knorr.",
      location: "Hamburg",
      tasks: [
        "Entwicklung dynamischer Webanwendungen (PWA) für das Web als auch für mobile Devices (Adobe AIR)",
        "Werbemittel-Entwicklung"
      ],
      clients: ["Unilever", "Red Bull", "Imperial Tobacco Group", "Studioline", "DPD"],
      companyType: "Werbeagentur"
    },
    {
      year: "Februar 2009 - September 2009",
      title: "Flash Developer",
      company: "Elephant Seven (Heute: Digitas Pixelpark)",
      description: "Nach meiner Ausbildung war ich zunächst als Flash Developer für die Erstellung interaktiver Werbemittel und Web-Specials für namhafte internationale Marken zuständig.",
      location: "Hamburg",
      clients: ["Mercedes", "Chrysler", "Storck", "Dräger", "Swarovski", "Red Bull", "Tchibo", "Lufthansa", "Palmers", "Montblanc"],
      companyType: "Werbeagentur"
    },
    {
      year: "September 2006 - Januar 2009",
      title: "Auszubildender: Mediengestaltung für Digital- und Printmedien (Fachrichtung Operating)",
      company: "Elephant Seven (Heute: Digitas Pixelpark)",
      description: "Während meiner Ausbildung zum Mediengestalter mit Schwerpunkt Frontend-Development erwarb ich umfassende Kenntnisse in der Entwicklung von Werbemitteln und Web-Specials für hochkarätige Kunden. Mit objektorientierter Programmierung in ActionScript 3.0 und der Entwicklung von Flash-Anwendungen konnte ich meine Fähigkeiten weiter vertiefen.",
      location: "Hamburg",
      clients: ["Mercedes", "Chrysler", "Storck", "Dräger", "Swarovski", "Red Bull", "Tchibo", "Lufthansa", "Palmers", "Montblanc"],
      companyType: "Werbeagentur"
    }
  ]
};

export default aboutData;